// src/components/VoiceChat.tsx
//
// Módulo Voz — shell cósmico.
//
// Capas:
//   1. CosmicField (fondo absolute) — partículas 3D orbitando un
//      agujero negro; reacciona al status y al giroscopio.
//   2. HUD flotante (overlay translúcido) — transcript, voice picker,
//      orbe central para abrir/cerrar el canal, input de fallback.
//
// Fix del layout anterior: el componente llenaba `calc(100vh - 180px)`
// y escapaba del shell en algunos viewports móviles, tapando el header
// global. Ahora usa `h-full min-h-[540px]` dentro de un contenedor que
// respeta la altura del module-route.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Hand, Mic, MicOff, Send, Smartphone, Trash2, Volume2, Wifi, WifiOff,
  Sparkles, X,
} from 'lucide-react';

import { useVoiceWebSocket, VOICES, type VoiceStatus } from '@/hooks/useVoiceWebSocket';
import CosmicField from '@/components/voice/CosmicField';
import { useGyroscope } from '@/components/voice/useGyroscope';


/* ─── status config ─── */
const STATUS_CFG: Record<VoiceStatus, { label: string; dotColor: string; ring: string; animate: boolean }> = {
  disconnected:  { label: 'Desconectado',   dotColor: 'bg-gray-400',    ring: 'ring-gray-500/40',   animate: false },
  connecting:    { label: 'Conectando',     dotColor: 'bg-amber-400',   ring: 'ring-amber-400/60',  animate: true },
  listening:     { label: 'Te escucho',     dotColor: 'bg-emerald-400', ring: 'ring-emerald-400/80',animate: true },
  user_speaking: { label: 'Escuchándote',   dotColor: 'bg-sky-400',     ring: 'ring-sky-400/90',    animate: true },
  thinking:      { label: 'Pensando',       dotColor: 'bg-[color:var(--acc2)]',  ring: 'ring-[color:var(--acc2)]/70', animate: true },
  speaking:      { label: 'Hablando',       dotColor: 'bg-amber-400',   ring: 'ring-amber-400/90',  animate: true },
  error:         { label: 'Error',          dotColor: 'bg-red-500',     ring: 'ring-red-500/60',    animate: false },
};


export default function VoiceChat() {
  const {
    status,
    messages,
    currentTranscript,
    isConnected,
    isChannelOpen,
    selectedVoice,
    activeTool,
    error,
    openChannel,
    closeChannel,
    sendText,
    changeVoice,
    interrupt,
    clearMessages,
  } = useVoiceWebSocket();

  const gyro = useGyroscope();
  const [textInput, setTextInput] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // audioLevel derivado del status — es lo único que expone el hook hoy.
  // Cuando haya AnalyserNode real esto se cambia por el RMS vivo.
  const audioLevel = useMemo(() => {
    if (status === 'user_speaking') return 0.7;
    if (status === 'speaking') return 0.85;
    if (status === 'listening') return 0.2;
    return 0.05;
  }, [status]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, currentTranscript]);

  const handleSendText = () => {
    if (!textInput.trim()) return;
    sendText(textInput);
    setTextInput('');
  };

  const toggleChannel = () => {
    if (isChannelOpen) closeChannel();
    else openChannel();
  };

  const cfg = STATUS_CFG[status];
  const hasTranscript = messages.length > 0 || !!currentTranscript;

  return (
    <div className="relative w-full h-full min-h-[540px] rounded-2xl overflow-hidden border border-white/5 bg-black">
      {/* ═══ Fondo cósmico (partículas 3D) ═══ */}
      <CosmicField
        status={status}
        tilt={gyro.tilt}
        audioLevel={audioLevel}
        dim={showTranscript}
      />

      {/* ═══ Overlay HUD ═══ */}
      <div className="absolute inset-0 flex flex-col pointer-events-none">

        {/* ── Top bar ── */}
        <header className="pointer-events-auto flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center gap-2.5 backdrop-blur-md bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
            <span className={`w-2 h-2 rounded-full ${cfg.dotColor} ${cfg.animate ? 'animate-pulse' : ''}`} />
            <span className="text-xs font-medium text-white/90">
              {activeTool ? `⚙ ${activeTool}` : cfg.label}
            </span>
            {isConnected ? (
              <Wifi size={11} className="text-emerald-400/80" />
            ) : (
              <WifiOff size={11} className="text-red-400/80" />
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {gyro.status === 'needs_permission' && (
              <button
                onClick={gyro.request}
                className="flex items-center gap-1 text-[11px] rounded-full px-2.5 py-1 backdrop-blur-md bg-white/5 border border-white/10 text-white/90 hover:bg-white/10 transition"
                title="Activa el giroscopio para 3D"
              >
                <Smartphone size={11} /> activar 3D
              </button>
            )}
            <select
              value={selectedVoice}
              onChange={(e) => changeVoice(e.target.value)}
              className="text-[11px] rounded-full px-2.5 py-1 backdrop-blur-md bg-white/5 border border-white/10 text-white/90 focus:outline-none focus:ring-1 focus:ring-emerald-400/60 appearance-none"
            >
              {VOICES.map((v) => (
                <option key={v.id} value={v.id} className="bg-black">
                  {v.label} {v.gender === 'F' ? '♀' : v.gender === 'M' ? '♂' : '◎'}
                </option>
              ))}
            </select>
            <button
              onClick={() => setShowTranscript((s) => !s)}
              className={`p-1.5 rounded-full backdrop-blur-md bg-white/5 border border-white/10 transition ${
                showTranscript ? 'ring-1 ring-emerald-400/60' : ''
              }`}
              title="Transcript"
            >
              <Sparkles size={13} className="text-white/80" />
            </button>
            <button
              onClick={clearMessages}
              className="p-1.5 rounded-full backdrop-blur-md bg-white/5 border border-white/10 hover:bg-white/10 transition"
              title="Limpiar"
            >
              <Trash2 size={13} className="text-white/70" />
            </button>
          </div>
        </header>

        {/* ── Error banner ── */}
        {error && (
          <div className="pointer-events-auto mx-4 mt-2 rounded-xl px-3 py-2 text-xs bg-red-500/20 border border-red-500/30 text-red-200 text-center">
            {error}
          </div>
        )}

        {/* ── Transcript drawer (abierto on demand) ── */}
        {showTranscript && (
          <div className="pointer-events-auto flex-1 mx-3 my-3 rounded-2xl overflow-hidden backdrop-blur-2xl bg-black/40 border border-white/10 flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
              <span className="text-[11px] uppercase tracking-wider text-white/60">Transcript</span>
              <button
                onClick={() => setShowTranscript(false)}
                className="p-1 rounded-full hover:bg-white/10"
              >
                <X size={14} className="text-white/70" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
              {!hasTranscript && (
                <div className="h-full flex flex-col items-center justify-center gap-2 text-white/40 text-center py-10">
                  <Mic size={28} />
                  <p className="text-sm">Nada por aquí todavía.</p>
                  <p className="text-xs">Abre el canal y habla.</p>
                </div>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[82%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-emerald-400/20 text-emerald-100 rounded-br-md border border-emerald-400/30'
                        : 'bg-white/10 text-white/90 rounded-bl-md border border-white/10'
                    }`}
                  >
                    <span className="block text-[10px] uppercase tracking-wider opacity-60 mb-0.5">
                      {msg.role === 'user' ? 'Tú' : 'Orión'}
                    </span>
                    <p className="whitespace-pre-wrap">{msg.text}</p>
                  </div>
                </div>
              ))}
              {currentTranscript && (
                <div className="flex justify-start">
                  <div className="max-w-[82%] rounded-2xl px-3 py-2 text-sm leading-relaxed bg-white/10 text-white/90 rounded-bl-md border border-white/10">
                    <span className="block text-[10px] uppercase tracking-wider opacity-60 mb-0.5">Orión</span>
                    <p className="whitespace-pre-wrap">
                      {currentTranscript}
                      <span className="animate-pulse">▊</span>
                    </p>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>
          </div>
        )}

        {/* ── Spacer so the orb is centered when transcript is closed ── */}
        {!showTranscript && <div className="flex-1" />}

        {/* ── Hint text cuando no hay transcript ── */}
        {!showTranscript && !hasTranscript && (
          <div className="pointer-events-none text-center text-white/50 text-sm mb-6 px-6">
            {isChannelOpen
              ? 'Canal abierto — habla cuando quieras'
              : 'Toca la singularidad para abrir canal'}
          </div>
        )}

        {/* ── Dock inferior: orbe + input ── */}
        <footer className="pointer-events-auto px-4 pb-5 pt-3 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex items-center justify-center gap-3 mb-3">
            {/* Interrumpir */}
            {status === 'speaking' && (
              <button
                onClick={interrupt}
                className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 backdrop-blur-md transition"
                title="Interrumpir"
              >
                <Hand size={18} className="text-red-300" />
              </button>
            )}

            {/* Orbe principal — horizonte de eventos */}
            <button
              onClick={toggleChannel}
              disabled={status === 'connecting'}
              className={`
                relative w-24 h-24 rounded-full
                flex items-center justify-center
                transition-all duration-500 select-none
                backdrop-blur-md bg-white/5
                ring-2 ${cfg.ring}
                ${status === 'connecting' ? 'cursor-wait' : 'cursor-pointer hover:bg-white/10'}
                ${status === 'disconnected' ? 'opacity-70' : ''}
              `}
              title={isChannelOpen ? 'Cerrar canal' : 'Iniciar canal'}
            >
              {/* Pulse rings */}
              {(status === 'listening' || status === 'user_speaking') && (
                <>
                  <span className="absolute inset-0 rounded-full animate-ping bg-emerald-400/20" />
                  <span className="absolute -inset-2 rounded-full animate-pulse bg-emerald-400/5" />
                </>
              )}
              {status === 'speaking' && (
                <span className="absolute -inset-2 rounded-full animate-pulse bg-amber-400/15" />
              )}

              <div className="relative z-10">
                {status === 'connecting' ? (
                  <div className="w-7 h-7 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                ) : status === 'thinking' ? (
                  <div className="w-7 h-7 border-2 border-[color:var(--acc1)]/40 border-t-[color:var(--acc1)] rounded-full animate-spin" />
                ) : status === 'speaking' ? (
                  <Volume2 size={30} className="text-amber-300" />
                ) : isChannelOpen ? (
                  <Mic size={30} className="text-emerald-300" />
                ) : (
                  <MicOff size={30} className="text-white/60" />
                )}
              </div>
            </button>
          </div>

          {/* Input de texto fallback */}
          <div className="flex max-w-lg mx-auto gap-2">
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendText()}
              placeholder="…o escribe"
              disabled={!isConnected}
              className="flex-1 rounded-full px-4 py-2 text-sm bg-white/5 border border-white/10 backdrop-blur-md placeholder:text-white/40 text-white focus:outline-none focus:ring-1 focus:ring-emerald-400/60 disabled:opacity-40"
            />
            <button
              onClick={handleSendText}
              disabled={!isConnected || !textInput.trim()}
              className="rounded-full w-10 h-10 flex items-center justify-center bg-emerald-400/20 hover:bg-emerald-400/30 border border-emerald-400/30 text-emerald-300 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Send size={15} />
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
